import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  mergeClasses,
  MessageBar,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import {
  Add16Regular,
  ArrowDown16Regular,
  ArrowUp16Regular,
  Delete16Regular,
  Edit16Regular,
  Eye16Regular,
} from "@fluentui/react-icons";
import {
  deleteObjectiveFromDiagnostic,
  getDiagnosticById,
  reorderObjectives,
} from "apps/diagnostics/api";
import { IDiagnostic } from "apps/diagnostics/models/IDiagnostic";
import {
  IObjective,
  OBJECTIVE_TYPE_AI_GENERATED,
  OBJECTIVE_TYPE_LABEL_AI_GENERATED,
  OBJECTIVE_TYPE_LABEL_LIKERT_SCALE,
  OBJECTIVE_TYPE_LABEL_PREDEFINED,
  OBJECTIVE_TYPE_LIKERT_SCALE,
  OBJECTIVE_TYPE_PREDEFINED,
  ObjectiveType,
  OBJECTIVE_TYPE_MULTIPLE_CHOICE,
  OBJECTIVE_TYPE_LABEL_MULTIPLE_CHOICE,
} from "apps/diagnostics/models/IObjective";
import { ConstantValues } from "common/constants";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import {
  IAdminListAction,
  IAdminRowAction,
} from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { ITableColumn } from "models/ITableColumn";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "store";
import { useSharedButtonStyles, useSharedTagStyles } from "theme/sharedStyles";
import { updateDiagnosticInSlice } from "../../../../redux/diagnosticsSlice";
import { EditObjective } from "./editDialog/EditObjective";
import { useStyles } from "./manageObjectives.styles";
import { IManageObjectivesProps } from "./manageObjectives.types";

const DESCRIPTION_PREVIEW_MAX_LENGTH = 40;
const SHIMMER_ROWS = 3;

export const ManageObjectives = (
  props: IManageObjectivesProps,
): JSX.Element => {
  const styles = useStyles();
  const buttonStyles = useSharedButtonStyles();
  const tagStyles = useSharedTagStyles();
  const dispatch = useDispatch();
  const params = useParams();
  const colStyles = useAdminColumnStyles();

  const routeId: string =
    (params.id as string) ||
    (params.diagnosticId as string) ||
    ConstantValues.EMPTY_STRING;
  const diagnosticId: string = props.diagnosticId ?? routeId;

  const diagnostics: IDiagnostic[] = useSelector(
    (state: RootState) => state.diagnosticsSlice.value,
  );
  const diagnosticFromStore: IDiagnostic | undefined = diagnostics.find(
    (d) => d.id === diagnosticId,
  );
  const [diagnostic, setDiagnostic] = useState<IDiagnostic | undefined>(
    diagnosticFromStore,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadingError] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );

  const [objectives, setObjectives] = useState<IObjective[]>(
    diagnostic?.objectives ?? [],
  );

  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [showRequiredQuestionsDialog, setShowRequiredQuestionsDialog] =
    useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState<
    string | undefined
  >(undefined);
  const dialogTriggerRef = React.useRef<HTMLElement | null>(null);

  const saveFocusAndRun = useCallback((fn: () => void) => {
    dialogTriggerRef.current = document.activeElement as HTMLElement;
    fn();
  }, []);

  const restoreFocus = useCallback(() => {
    dialogTriggerRef.current?.focus();
    dialogTriggerRef.current = null;
  }, []);

  const selectedObjective = useMemo(
    () => objectives.find((o) => o._id === selectedObjectiveId),
    [objectives, selectedObjectiveId],
  );

  const sortedObjectives = useMemo(() => {
    return [...objectives].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [objectives]);

  const ITEMS_PER_PAGE = 10;
  const [pageIndex, setPageIndex] = useState(0);
  const pageCount = Math.max(
    1,
    Math.ceil(sortedObjectives.length / ITEMS_PER_PAGE),
  );
  const paginatedObjectives = useMemo(() => {
    const start = pageIndex * ITEMS_PER_PAGE;
    return sortedObjectives.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedObjectives, pageIndex]);

  useEffect(() => {
    if (pageIndex >= pageCount) setPageIndex(Math.max(0, pageCount - 1));
  }, [pageIndex, pageCount]);

  useEffect((): void => {
    const fetchDiagnostic = async () => {
      setIsLoading(true);
      try {
        const result = await getDiagnosticById({
          id: diagnosticId ?? ConstantValues.EMPTY_STRING,
        });
        setDiagnostic(result);
      } catch (error) {
        console.error("Error fetching diagnostic:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!diagnosticId) {
      return;
    }

    if (!diagnosticFromStore) {
      fetchDiagnostic();
    }
  }, [diagnosticId, diagnosticFromStore]);

  useEffect((): void => {
    const init = async () => {
      if (!diagnosticId) {
        setLoadingError("No diagnostic id found in URL.");
        return;
      }

      if (diagnostic) {
        setObjectives(diagnostic.objectives ?? []);
        return;
      }

      try {
        setIsLoading(true);
        setLoadingError(ConstantValues.EMPTY_STRING);
        const diagnosticById: IDiagnostic = await getDiagnosticById({
          id: diagnosticId,
        });
        if (diagnosticById) {
          dispatch(updateDiagnosticInSlice(diagnosticById));
          setObjectives(diagnosticById.objectives ?? []);
        } else {
          setLoadingError("Diagnostic not found.");
        }
      } catch (e) {
        console.error(e);
        setLoadingError("Failed to load diagnostic.");
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [diagnosticId]);

  useEffect((): void => {
    if (!diagnostic) {
      return;
    }

    setObjectives(diagnostic.objectives ?? []);
  }, [diagnostic?.id, diagnostic?.objectives]);

  const refreshDiagnostic = async () => {
    const updated = await getDiagnosticById({ id: diagnosticId });
    if (updated) {
      setDiagnostic(updated);
      setObjectives(updated.objectives ?? []);
      dispatch(updateDiagnosticInSlice(updated));
      props.onObjectivesChange(updated.objectives ?? []);
    }
  };

  const handleCreateClick = (): void => {
    saveFocusAndRun(() => {
      setSelectedObjectiveId(undefined);
      setIsEditPopupOpen(true);
    });
  };

  const onDeleteConfirmed = async (): Promise<void> => {
    if (!selectedObjective || !diagnosticId) {
      return;
    }

    try {
      await deleteObjectiveFromDiagnostic({
        id: diagnosticId,
        questionNumber: selectedObjective.questionNumber,
      });
      const updated = await getDiagnosticById({ id: diagnosticId });
      if (updated) {
        dispatch(updateDiagnosticInSlice(updated));
        setObjectives(updated.objectives ?? []);
        props.onObjectivesChange(updated.objectives ?? []);
      }
    } catch (e) {
      console.error("Error deleting objective:", e);
    } finally {
      setShowDeleteDialog(false);
      setSelectedObjectiveId(undefined);
      restoreFocus();
    }
  };

  const handleDialogDismiss = async (saved?: IObjective) => {
    setIsEditPopupOpen(false);
    restoreFocus();
    if (!saved) return;

    setObjectives((prev) => {
      const exists = prev.some(
        (o) => o.questionNumber === saved.questionNumber,
      );
      return exists
        ? prev.map((o) =>
            o.questionNumber === saved.questionNumber ? saved : o,
          )
        : [...prev, saved];
    });

    setSelectedObjectiveId(saved._id);

    try {
      await refreshDiagnostic();
    } catch (e) {
      console.error("Error refreshing diagnostic:", e);
    }
  };

  const moveObjective = async (item: IObjective, direction: -1 | 1) => {
    const currentIndex = sortedObjectives.findIndex((o) => o._id === item._id);
    if (currentIndex === -1) return;

    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= sortedObjectives.length) return;

    const modelToMove = sortedObjectives[currentIndex];
    const targetModel = sortedObjectives[targetIndex];

    const updatedModelA = { ...modelToMove, order: targetModel.order };
    const updatedModelB = { ...targetModel, order: modelToMove.order };

    const updatedList = [...sortedObjectives];
    updatedList[currentIndex] = updatedModelB;
    updatedList[targetIndex] = updatedModelA;
    updatedList.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    try {
      await reorderObjectives({
        id: diagnosticId,
        orderList: updatedList.map((objective: IObjective) => ({
          id: objective._id,
          order: objective.order ?? 0,
        })),
      });
      setObjectives(updatedList);
    } catch (e) {
      console.error("Error reordering objectives:", e);
    }
  };

  const getObjectiveTypeLabel = (type: ObjectiveType | undefined): string => {
    switch (type) {
      case OBJECTIVE_TYPE_PREDEFINED:
        return OBJECTIVE_TYPE_LABEL_PREDEFINED;
      case OBJECTIVE_TYPE_AI_GENERATED:
        return OBJECTIVE_TYPE_LABEL_AI_GENERATED;
      case OBJECTIVE_TYPE_LIKERT_SCALE:
        return OBJECTIVE_TYPE_LABEL_LIKERT_SCALE;
      case OBJECTIVE_TYPE_MULTIPLE_CHOICE:
        return OBJECTIVE_TYPE_LABEL_MULTIPLE_CHOICE;
      default:
        return ConstantValues.EMPTY_STRING;
    }
  };

  const getObjectiveTypeClassName = (
    type: ObjectiveType | undefined,
  ): string => {
    switch (type) {
      case OBJECTIVE_TYPE_PREDEFINED:
        return tagStyles.tagSuccess;
      case OBJECTIVE_TYPE_AI_GENERATED:
        return tagStyles.tagAccent;
      case OBJECTIVE_TYPE_LIKERT_SCALE:
        return tagStyles.tagInfo;
      case OBJECTIVE_TYPE_MULTIPLE_CHOICE:
        return tagStyles.tagWarning;
      default:
        return tagStyles.tagInfo;
    }
  };

  const columns: ITableColumn<IObjective>[] = useMemo(
    () => [
      {
        columnId: "name",
        displayValue: "Name",
        renderHeaderCell: () => "Name",
        renderCell: (item: IObjective) => (
          <Tooltip
            content={item.name ?? ConstantValues.EMPTY_STRING}
            relationship="label"
          >
            <span>{item.name ?? ConstantValues.EMPTY_STRING}</span>
          </Tooltip>
        ),
        compare: (a, b) =>
          (a.name ?? ConstantValues.EMPTY_STRING).localeCompare(
            b.name ?? ConstantValues.EMPTY_STRING,
          ),
        className: mergeClasses(colStyles.normal, styles.cellContent),
      },
      {
        columnId: "type",
        displayValue: "Type",
        renderHeaderCell: () => "Type",
        renderCell: (item: IObjective) => {
          const isPredefined = item.objectiveType === OBJECTIVE_TYPE_PREDEFINED;
          return (
            <span
              className={mergeClasses(
                tagStyles.tag,
                getObjectiveTypeClassName(item.objectiveType),
              )}
            >
              {getObjectiveTypeLabel(item.objectiveType)}
            </span>
          );
        },
        compare: (a, b) => {
          return getObjectiveTypeLabel(a.objectiveType).localeCompare(
            getObjectiveTypeLabel(b.objectiveType),
          );
        },
        className: mergeClasses(colStyles.medium, styles.cellContent),
      },
      {
        columnId: "description",
        displayValue: "Description",
        renderHeaderCell: () => "Description",
        renderCell: (item: IObjective) => {
          const isAIGenerated =
            item.objectiveType === OBJECTIVE_TYPE_AI_GENERATED;
          const fullText = isAIGenerated
            ? (item.description ?? ConstantValues.EMPTY_STRING)
            : (item.sampleQuestions ?? []).map((q) => q.text).join(" • ");

          if (!fullText) {
            return <span>{ConstantValues.EMPTY_STRING}</span>;
          }

          const preview =
            fullText.length > DESCRIPTION_PREVIEW_MAX_LENGTH
              ? `${fullText.slice(0, DESCRIPTION_PREVIEW_MAX_LENGTH).trimEnd()}…`
              : fullText;

          return (
            <Tooltip content={fullText} relationship="label">
              <span className={styles.descriptionPreview}>{preview}</span>
            </Tooltip>
          );
        },
        compare: (a, b) =>
          (a.description ?? ConstantValues.EMPTY_STRING).localeCompare(
            b.description ?? ConstantValues.EMPTY_STRING,
          ),
        className: mergeClasses(colStyles.wide, styles.cellContent),
      },
    ],
    [colStyles, tagStyles, styles],
  );

  const rowActions: IAdminRowAction<IObjective>[] = useMemo(
    () => [
      {
        icon: <ArrowUp16Regular />,
        tooltip: "Move Up",
        onClick: (item: IObjective) => moveObjective(item, -1),
        disabled: (item: IObjective) =>
          sortedObjectives.findIndex((o) => o._id === item._id) === 0,
      },
      {
        icon: <ArrowDown16Regular />,
        tooltip: "Move Down",
        onClick: (item: IObjective) => moveObjective(item, 1),
        disabled: (item: IObjective) => {
          const idx = sortedObjectives.findIndex((o) => o._id === item._id);
          return idx === sortedObjectives.length - 1;
        },
      },
      {
        icon: <Edit16Regular />,
        tooltip: "Edit",
        onClick: (item: IObjective) => {
          saveFocusAndRun(() => {
            setSelectedObjectiveId(item._id);
            setIsEditPopupOpen(true);
          });
        },
      },
      {
        icon: <Delete16Regular />,
        tooltip: "Delete",
        onClick: (item: IObjective) => {
          saveFocusAndRun(() => {
            setSelectedObjectiveId(item._id);
            setShowDeleteDialog(true);
          });
        },
      },
      {
        icon: <Eye16Regular />,
        tooltip: "View Questions",
        onClick: (item: IObjective) => {
          saveFocusAndRun(() => {
            setSelectedObjectiveId(item._id);
            setShowRequiredQuestionsDialog(true);
          });
        },
        disabled: (item: IObjective) =>
          !item.sampleQuestions || item.sampleQuestions.length === 0,
      },
    ],
    [sortedObjectives],
  );

  const headerActions: IAdminListAction[] = useMemo(
    () => [
      {
        label: "Create",
        icon: <Add16Regular />,
        onClick: handleCreateClick,
        appearance: "primary",
      },
    ],
    [objectives.length],
  );

  const emptyStateMessage = useMemo(
    () => "No objectives yet. Create one to get started.",
    [],
  );

  if (!diagnosticId) {
    return (
      <div className={styles.wrapper}>
        <MessageBar intent="error">No diagnostic id found in URL.</MessageBar>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.wrapper}>
        <MessageBar intent="error">{loadError}</MessageBar>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <AdminListPage<IObjective>
        title="Objectives"
        items={sortedObjectives}
        paginatedItems={paginatedObjectives}
        columns={columns}
        isLoading={isLoading}
        headerActions={headerActions}
        rowActions={rowActions}
        getRowId={(item) => item._id ?? `obj-${item.questionNumber}`}
        emptyStateMessage={emptyStateMessage}
        shimmerRows={SHIMMER_ROWS}
        noPadding
        actionsWidth="180px"
        selectedPageIndex={pageIndex}
        pageCount={pageCount}
        totalItemsCount={sortedObjectives.length}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setPageIndex}
      />
      <div className={styles.hintText} aria-live="polite">
        <span>
          During the interview, the objectives will display in the order above.
        </span>
      </div>
      {showRequiredQuestionsDialog && (
        <Dialog
          open={showRequiredQuestionsDialog}
          modalType="modal"
          onOpenChange={(_, data) => {
            if (!data.open) {
              setShowRequiredQuestionsDialog(false);
              restoreFocus();
            }
          }}
        >
          <DialogSurface>
            <DialogBody>
              <DialogTitle>
                {selectedObjective?.objectiveType ===
                OBJECTIVE_TYPE_AI_GENERATED
                  ? "Starter Question"
                  : "Questions"}
              </DialogTitle>
              <DialogContent>
                {selectedObjective?.sampleQuestions &&
                selectedObjective?.sampleQuestions?.length > 0 ? (
                  <div className={styles.sampleQuestionsGrid}>
                    {selectedObjective.sampleQuestions.map((q, i) => (
                      <div key={i} className={styles.sampleQuestionItem}>
                        <Text size={200}>{q.text}</Text>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Text size={200} italic>
                    No questions.
                  </Text>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  appearance="secondary"
                  className={buttonStyles.primaryButton}
                  onClick={() => {
                    setShowRequiredQuestionsDialog(false);
                    restoreFocus();
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
      {isEditPopupOpen && (
        <EditObjective
          isOpen={isEditPopupOpen}
          onDismiss={handleDialogDismiss}
          diagnosticId={diagnosticId}
          objective={selectedObjective}
        />
      )}

      {showDeleteDialog && (
        <ActionDialog
          isVisible={showDeleteDialog}
          title="Delete Objective"
          onConfirmation={onDeleteConfirmed}
          onDismiss={() => {
            setShowDeleteDialog(false);
            restoreFocus();
          }}
          confirmationMessage="Delete"
          dismissMessage="Cancel"
          message="Are you sure you want to delete this objective?"
        />
      )}
    </div>
  );
};
